import React from 'react'
import { useAppSelector } from '../../../../../../../../../../app/hooks'
import { InitState } from '../../../../../../../../../../redux/reducers/root/initialState'
import {
  AttachmentData,
  AttachmentDataWithRequiredAttachment,
  AttachmentType,
} from '../../../../../../../../../../@types/types'
import { PictureCarousel } from './PictureCarousel'
import { useMultimediaDetailsModalContext } from '../../../../multimediaDetailsModalContext/multimediaModalsContext'
import { createAndTriggerPluploadFileInput } from '../../../utils/fileUploadUtils'
import styles from './MobileMultimediaPreview.module.css'
import { useFeatureSwitch } from '../../../../../../../../../../utils/features/FeatureSwitchContext'
import { FeatureSwitch } from '../../../../../../../../../../utils/features/featureSwitches'
import { isWebView } from '../../../../../../../../../../utils/navigation'
import { UploadedImagesInlineGallery } from './UploadedImagesInlineGallery'
import { PicturesUploadTip } from './PicturesUploadTip'
import { usePictureSelection } from './usePictureSelection'

interface MobileMultimediaPreviewProps {
  dropzoneId: string
  pluploadRef?: React.Ref<HTMLDivElement>
  onFilesSelected: (files: FileList) => void
}

const EMPTY_ATTACHMENTS: AttachmentData[] = []

export const WebViewMultimediaPreview: React.FC<MobileMultimediaPreviewProps> = ({
  dropzoneId,
  pluploadRef,
  onFilesSelected,
}) => {
  const attachments = useAppSelector((state: InitState) => state.multimediaFeature.attachments || EMPTY_ATTACHMENTS)
  const { openModalForEditClick, openDetailsModalFromCarousel } = useMultimediaDetailsModalContext()
  const isMobileFlow = useAppSelector((state) => state.flowFeature.flowInformation.isMobileFlow)
  const { isActive: isNewImageUploadEnabled } = useFeatureSwitch(
    FeatureSwitch.PPA_13285_RENT_SALE_MOBILE_NEW_IMAGE_UPLOAD,
    isWebView() && isMobileFlow
  )

  const pictureAttachments = attachments.filter(
    (attachment) => attachment.attachmentType === AttachmentType.PICTURE && attachment.attachment?.url
  ) as AttachmentDataWithRequiredAttachment[]
  const { selectedImageId, selectImage } = usePictureSelection(pictureAttachments)

  if (pictureAttachments.length === 0) {
    return null
  }

  const handleEditClick = (attachment: AttachmentDataWithRequiredAttachment) => {
    if (isNewImageUploadEnabled) {
      openDetailsModalFromCarousel(pictureAttachments, pictureAttachments.indexOf(attachment))
      return
    }

    // Find the index of the clicked attachment in the full attachments array
    const attachmentIndex = attachments.findIndex((att) => att === attachment)
    openModalForEditClick(attachmentIndex)
  }

  const handleAddClick = () => {
    createAndTriggerPluploadFileInput()
  }

  return (
    <>
      <div ref={pluploadRef} style={{ display: 'none' }} />
      <div className={`relative w-full ${styles.previewContainer}`}>
        <PictureCarousel
          attachments={pictureAttachments}
          onEditClick={handleEditClick}
          onAddClick={handleAddClick}
          selectedImageId={selectedImageId}
          onImageSelect={selectImage}
          showAddButton={!isNewImageUploadEnabled}
          dropzoneId={dropzoneId}
        />
        {isNewImageUploadEnabled && (
          <div className={'margin-top-l'}>
            <UploadedImagesInlineGallery
              pictureAttachments={pictureAttachments}
              onFilesSelected={onFilesSelected}
              selectedImageId={selectedImageId}
              onImageSelect={selectImage}
            />
            <PicturesUploadTip pictureCount={pictureAttachments.length} />
          </div>
        )}
      </div>
    </>
  )
}
